/**
 * Skill Generation API
 * AI-assisted skill configuration generation from instructions
 */

import apiClient from './client';

// Longer timeout for AI operations (2 minutes)
const AI_TIMEOUT = 120000;

// ============================================================================
// Types
// ============================================================================

export interface SkillGenerateRequest {
  instructions: string;
  model: string;
  language?: string;
  context?: string;
}

export interface SkillGenerateResponse {
  skill_id: string;
  name: string;
  description: string;
  keywords: string[];
  patterns: string[];
  example_queries: string[];
  exclusion_group: string | null;
  force_response_type: 'quick' | 'deep' | null;
  explanation: string;
}

export interface PatternGenerateRequest {
  instructions: string;
  keywords?: string[];
  example_queries?: string[];
  model: string;
  language?: string;
}

export interface PatternGenerateResponse {
  patterns: string[];
  explanation: string;
}

// ============================================================================
// API Client
// ============================================================================

export const skillGenerationApi = {
  /**
   * Generate skill configuration from instructions using AI
   */
  generate: async (data: SkillGenerateRequest): Promise<SkillGenerateResponse> => {
    const response = await apiClient.post<SkillGenerateResponse>(
      '/skill-generation/generate',
      data,
      { timeout: AI_TIMEOUT }
    );
    return response.data;
  },

  /**
   * Generate regex patterns for skill matching using AI
   */
  generatePatterns: async (data: PatternGenerateRequest): Promise<PatternGenerateResponse> => {
    const response = await apiClient.post<PatternGenerateResponse>(
      '/skill-generation/generate-patterns',
      data,
      { timeout: AI_TIMEOUT }
    );
    return response.data;
  },
};
